import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Media } from "@zivin-labs/site-content";
import { Figure } from "../Figure";
import { ROUTES } from "../../lib/routes";
import { UG_BRANCH_COUNT, PG_PROGRAMME_COUNT } from "../../lib/departments";
import type { HeroVariant } from "../../lib/tweaks";
import { AUDITORIUM_PHOTOS, LIBRARY_PHOTOS } from "../../lib/campusLifeMedia";
import { MAIN_BLOCK } from "../../lib/campusMedia";
import { PLACEMENT_BATCHES, employerCount, summaryFigures } from "../../lib/placements";
import { latestPlacementYear } from "../../lib/placementTrends";
import { ALUMNI_MEET } from "../../lib/alumniMeet";

const LATEST_PLACEMENTS = PLACEMENT_BATCHES[0];
const PLACEMENT_FIGURES = summaryFigures(LATEST_PLACEMENTS);
const PLACEMENT_EMPLOYERS = employerCount(LATEST_PLACEMENTS);
const PLACED = latestPlacementYear().placed;

/** One ingested photo out of a pool, by its key's last segment. */
function shot(pool: Media[], slug: string): Media | undefined {
  return pool.find((m) => m.url.endsWith(`/${slug}`));
}

/**
 * Auto-advancing slide index, shared by the two rotating variants (A and D).
 * Holds still for visitors who ask for reduced motion — the pager still works,
 * so nothing becomes unreachable, it just stops moving on its own.
 */
function useSlideshow(count: number, ms = 7000) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (count < 2) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % count), ms);
    return () => clearInterval(t);
  }, [count, ms]);
  return [idx, setIdx] as const;
}

/**
 * Hero imagery, from the institute's own photographs already ingested for
 * /campus-life. Chosen for what each layout needs rather than reused blindly:
 *
 *   CENTRE_DAY    the Cultural Centre in daylight, marigold beds in the
 *                 foreground — the one frame that reads as "campus" and
 *                 survives a tall 4:5 crop.
 *   CENTRE_NIGHT  the same building lit for an evening event; the only frame
 *                 dark enough to carry white type over it (variant D).
 *   HALL          the 700-seat hall from the balcony — apt behind the
 *                 placement slide, since that is where a drive that draws the
 *                 whole cohort is held.
 *   LIBRARY       the Central Library block behind its garden path.
 *
 * The gallery's 142 campus frames were considered and passed over: they are
 * indoor event photography (seminars, prize-givings), not architecture.
 */
const CENTRE_DAY = shot(AUDITORIUM_PHOTOS, "auditorium-19.jpg");
const CENTRE_NIGHT = shot(AUDITORIUM_PHOTOS, "auditorium-17.jpg");
// From the placement cell's own photo set (not /campus-life): the auditorium
// from the balcony, mid-drive — placement-specific, and its 2560×1707 frame
// matches CENTRE_DAY's 2560×1706 almost exactly.
const HALL: Media = {
  url: "sites/biet/placement/auditorium-06.jpg",
  alt: "The auditorium seen from the balcony, tiered seating facing the stage",
  width: 2560,
  height: 1707,
};
const LIBRARY = shot(LIBRARY_PHOTOS, "library-01.png");

const SLIDES = [
  {
    eyebrow: "Admissions · AY 2026-27",
    title: "Admissions are\nnow open",
    sub: "Begin your journey at one of Karnataka's premier engineering institutions. Apply now and shape your future with innovation and excellence.",
    cta1: "Apply now",
    cta2: "Learn more",
    media: MAIN_BLOCK,
    placeholder: "BIET MAIN BLOCK · CAMPUS PHOTO",
  },
  {
    eyebrow: `Placements · ${LATEST_PLACEMENTS.batch} batch`,
    title: `${PLACED} placed.\n${PLACEMENT_EMPLOYERS} companies\non campus.`,
    sub: `Highest CTC ₹${PLACEMENT_FIGURES.highest} LPA, average ₹${PLACEMENT_FIGURES.average} LPA. TCS, Cognizant — recruiting at BIET, year after year.`,
    cta1: "Placement report",
    cta2: "Recruiter portal",
    media: HALL,
    placeholder: "PLACEMENT DRIVE · GROUP PHOTO",
  },
  {
    eyebrow: "Now Autonomous",
    title: "Engineering\nfor Karnataka.\nFor the world.",
    sub: "BIET attains autonomous status — 15 research centres, NAAC ‘A’ grade, NBA accredited and recognized by UGC under 2(f) and 12(B).",
    cta1: "Explore research",
    cta2: "Faculty directory",
    media: LIBRARY,
    placeholder: "AI LAB · STUDENTS WORKING",
  },
];

function HeroA() {
  const [idx, setIdx] = useSlideshow(SLIDES.length);
  const s = SLIDES[idx];
  return (
    <section className="hero hero-a">
      <div className="hero-bg">
        <Figure
          media={s.media}
          fallbackLabel={s.placeholder}
          kind="dark"
          aspect="auto"
          priority
          sizes="100vw"
          style={{ position: "absolute", inset: 0 }}
        />
        <div className="hero-scrim" />
      </div>
      <div className="container hero-content">
        <div className="hero-eyebrow">
          <span className="hero-eyebrow-dot"></span>
          {s.eyebrow}
        </div>
        <h1 className="hero-title">
          {s.title.split("\n").map((t, i) => (
            <span key={i}>
              {t}
              <br />
            </span>
          ))}
        </h1>
        <p className="hero-sub">{s.sub}</p>
        <div className="hero-ctas">
          <Link className="btn btn-primary" to={ROUTES.admissions}>
            {s.cta1} <span>→</span>
          </Link>
          <a className="btn btn-on-dark" href="#about">
            {s.cta2} <span>→</span>
          </a>
        </div>
        <div className="hero-pager">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              className={"hero-pager-dot " + (i === idx ? "active" : "")}
              onClick={() => setIdx(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
          <span className="hero-pager-meta">
            {String(idx + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
          </span>
        </div>
      </div>
    </section>
  );
}

function HeroB() {
  return (
    <section className="hero-b">
      <div className="container hero-b-grid">
        <div className="hero-b-copy">
          <div className="kicker">Admissions · AY 2026-27</div>
          <h1>
            <span className="line-reveal">
              <span>Engineered for</span>
            </span>
            <br />
            <span className="line-reveal">
              <span>what comes next.</span>
            </span>
          </h1>
          <p>
            Established 1979. One of the oldest and most prestigious engineering institutions in
            Karnataka — now autonomous, NAAC ‘A’ grade, and home to {UG_BRANCH_COUNT} UG branches
            and {PG_PROGRAMME_COUNT} PG programmes.
          </p>
          <div className="hero-b-ctas">
            <Link className="btn btn-primary" to={ROUTES.admissions}>
              Apply now <span>→</span>
            </Link>
            <Link className="btn-link" to={ROUTES.academics}>
              View programs <span className="arr">→</span>
            </Link>
          </div>
          <div className="hero-b-stats">
            <div>
              <b data-count="1979" data-format="plain">
                0
              </b>
              <span>Established</span>
            </div>
            <div>
              <b data-count={UG_BRANCH_COUNT}>0</b>
              <span>UG branches</span>
            </div>
            <div>
              <b>NAAC </b>
              <span>Accredited</span>
            </div>
            <div>
              <b>Autonomous</b>
              <span>Since 2024</span>
            </div>
          </div>
        </div>
        <div className="hero-b-image">
          <Figure
            media={MAIN_BLOCK}
            fallbackLabel="HERO · CAMPUS WIDE SHOT"
            aspect="4/5"
            sizes="(max-width: 900px) 100vw, 42vw"
            priority
          />
          <div className="hero-b-badge">
            <div
              className="num engraved engraved--on-gold"
              data-count="1979"
              data-format="plain"
            >
              0
            </div>
            <div className="lbl">
              Established
              <br />
              Davanagere
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroC() {
  return (
    <section className="hero-c">
      <div className="container">
        <div className="masthead">Volume 47 · 2026 — 27</div>
        <h1 className="hero-c-title">
          A Karnataka
          <br />
          institution.
          <br />
          <em>Engineering since 1979.</em>
        </h1>
        <div className="hero-c-meta">
          <div>
            <div className="kicker">Now reading</div>
            <p>
              Admissions for the 2026 — 27 academic year are open across all UG, PG and research
              programs.
            </p>
            <Link
              className="btn btn-primary"
              to={ROUTES.admissions}
              style={{ marginTop: 16 }}
            >
              Apply now <span>→</span>
            </Link>
          </div>
          <div className="hero-c-grid">
            <Figure media={MAIN_BLOCK} fallbackLabel="MAIN BLOCK" aspect="3/4" sizes="30vw" priority />
            <Figure media={CENTRE_DAY} fallbackLabel="SSM CULTURAL CENTRE" aspect="3/4" sizes="30vw" />
            <Figure media={HALL} fallbackLabel="STUDENTS · QUAD" aspect="3/4" sizes="30vw" />
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * D — Poster. Three photographs of the campus, each behind its own held
 * statement — the same three messages variant A rotates through (admissions,
 * placements, autonomous status), paired with the matching campus frame so
 * the claim and the photo change together.
 *
 * All three frames are in the DOM at once and cross-faded by opacity rather
 * than swapping one <img>'s source, which would blink white on a slow
 * connection every seventh second. Only the first is fetch-priority high.
 */
const POSTER_SLIDES: Array<{
  media: Media | undefined;
  label: string;
  caption: string;
  eyebrow: string;
  title: string;
  sub: string;
  /** Event slides show their own poster beside the copy instead of a bare photo. */
  poster?: { url: string; alt: string; width: number; height: number };
  cta?: { label: string; to: string };
  /** Kept in the list but not shown; drop the flag to bring the slide back. */
  hidden?: boolean;
}> = [
  {
    media: CENTRE_NIGHT,
    label: "SSM CULTURAL CENTRE · EVENING",
    caption: "Global Alumni Meet, 19 December 2026",
    eyebrow: `Alumni · ${ALUMNI_MEET.dateLabel}`,
    title: `${ALUMNI_MEET.title}\n${ALUMNI_MEET.kannada}`,
    sub: `${ALUMNI_MEET.tagline} ${ALUMNI_MEET.weekday}, ${ALUMNI_MEET.timeLabel} at the ${ALUMNI_MEET.venue}, ${ALUMNI_MEET.place}.`,
    poster: ALUMNI_MEET.poster,
    cta: { label: "About the meet", to: `${ROUTES.alumni}#global-meet` },
  },
  {
    // Same photo as the About page hero and the closing "Applications open"
    // strip, per request — the main academic block, not the Cultural Centre.
    media: MAIN_BLOCK,
    label: "BIET MAIN BLOCK",
    caption: "The main academic block, seen across the front lawn",
    eyebrow: "Admissions · AY 2026-27",
    title: "Admissions are\nnow open",
    sub: "Begin your journey at one of Karnataka's premier engineering institutions. Apply now and shape your future with innovation and excellence.",
    hidden: true,
  },
  {
    media: HALL,
    label: "PLACEMENT DRIVE · GROUP PHOTO",
    caption: "The 700-seat hall, addressing a placement drive",
    eyebrow: `Placements · ${LATEST_PLACEMENTS.batch} batch`,
    title: `${PLACED} placed.\n${PLACEMENT_EMPLOYERS} companies\non campus.`,
    sub: `Highest CTC ₹${PLACEMENT_FIGURES.highest} LPA, average ₹${PLACEMENT_FIGURES.average} LPA. TCS, Cognizant — recruiting at BIET, year after year.`,
  },
  {
    media: LIBRARY,
    label: "CENTRAL LIBRARY",
    caption: "The Central Library and its garden",
    eyebrow: "Now Autonomous",
    title: "Engineering\nfor Karnataka.\nFor the world.",
    sub: "BIET attains autonomous status — 15 research centres, NAAC ‘A’ grade, NBA accredited and recognized by UGC under 2(f) and 12(B).",
  },
];

const SHOWN_SLIDES = POSTER_SLIDES.filter((s) => !s.hidden);

function HeroD() {
  const [idx, setIdx] = useSlideshow(SHOWN_SLIDES.length);
  const s = SHOWN_SLIDES[idx];
  return (
    <section className="hero-d">
      <div className="hero-d-bg">
        {SHOWN_SLIDES.map((slide, i) => (
          <Figure
            key={slide.label}
            media={slide.media}
            fallbackLabel={slide.label}
            kind="dark"
            aspect="auto"
            priority={i === 0}
            sizes="100vw"
            className={"hero-d-frame" + (i === idx ? " is-on" : "")}
            style={{ position: "absolute", inset: 0 }}
          />
        ))}
        <div className="hero-d-scrim" />
      </div>
      <div className={"container hero-d-content" + (s.poster ? " has-poster" : "")}>
       <div className="hero-d-copy">
        <div className="hero-d-eyebrow">{s.eyebrow}</div>
        <h1 className="hero-d-title">
          {s.title.split("\n").map((t, i) => (
            <span key={i}>
              {t}
              <br />
            </span>
          ))}
        </h1>
        <p className="hero-d-sub">{s.sub}</p>
        <div className="hero-d-ctas">
          {s.cta ? (
            <Link className="btn btn-primary" to={s.cta.to}>
              {s.cta.label} <span>→</span>
            </Link>
          ) : (
            <Link className="btn btn-primary" to={ROUTES.admissions}>
              Apply now <span>→</span>
            </Link>
          )}
          {!s.cta && (
            <Link className="btn btn-on-dark" to={ROUTES.campusLife}>
              Campus life <span>→</span>
            </Link>
          )}
        </div>
       </div>
        {s.poster && (
          <Link className="hero-d-poster" to={`${ROUTES.alumni}#global-meet`} tabIndex={-1} aria-hidden="true">
            <img
              src={s.poster.url}
              alt=""
              width={s.poster.width}
              height={s.poster.height}
            />
          </Link>
        )}
        {/* Same pager as variant A, so the control behaves identically in both
            and inherits its mobile sizing. The caption names the frame, which
            the dots alone cannot. */}
        <div className="hero-pager">
          {SHOWN_SLIDES.map((slide, i) => (
            <button
              key={slide.label}
              className={"hero-pager-dot " + (i === idx ? "active" : "")}
              onClick={() => setIdx(i)}
              aria-label={slide.caption}
              aria-current={i === idx}
            />
          ))}
          <span className="hero-pager-meta">{s.caption}</span>
        </div>
      </div>
      <div className="hero-d-rail">
        <div className="container hero-d-rail-inner">
          <div>
            <b>1979</b>
            <span>Established</span>
          </div>
          <div>
            <b>63</b>
            <span>Acre campus</span>
          </div>
          <div>
            <b>
              {UG_BRANCH_COUNT} + {PG_PROGRAMME_COUNT}
            </b>
            <span>UG + PG programmes</span>
          </div>
          <div>
            <b>2024</b>
            <span>Autonomous since</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * E — Mosaic. Copy beside a four-frame mosaic of the campus, and the only
 * variant that opens onto the photo gallery rather than the prospectus.
 */
const MOSAIC: Array<{ media: Media | undefined; label: string; cls: string }> = [
  { media: MAIN_BLOCK, label: "BIET MAIN BLOCK", cls: "hero-e-tile--tall" },
  { media: CENTRE_DAY, label: "SSM CULTURAL CENTRE", cls: "" },
  { media: HALL, label: "THE 700-SEAT HALL", cls: "" },
  { media: CENTRE_NIGHT, label: "CULTURAL CENTRE · EVENING", cls: "hero-e-tile--wide" },
];

function HeroE() {
  return (
    <section className="hero-e">
      <div className="container hero-e-grid">
        <div className="hero-e-copy">
          <div className="kicker">Davanagere · Since 1979</div>
          {/* No hard break: at 76px the forced line still wrapped, giving three
              ragged lines. `text-wrap: balance` evens them at every width. */}
          <h1>A campus you can walk through.</h1>
          <p>
            Sixty-three acres in central Karnataka — {UG_BRANCH_COUNT} UG branches,{" "}
            {PG_PROGRAMME_COUNT} PG programmes, two hostels, a 700-seat cultural centre and a
            library holding over 1.5 lakh volumes.
          </p>
          <div className="hero-e-ctas">
            <Link className="btn btn-primary" to={ROUTES.admissions}>
              Apply now <span>→</span>
            </Link>
            <Link className="btn-link" to={ROUTES.gallery}>
              Photo gallery <span className="arr">→</span>
            </Link>
          </div>
        </div>
        <div className="hero-e-mosaic">
          {MOSAIC.map((t, i) => (
            <Figure
              key={t.label}
              media={t.media}
              fallbackLabel={t.label}
              aspect="auto"
              className={"hero-e-tile " + t.cls}
              sizes="(max-width: 900px) 50vw, 25vw"
              priority={i === 0}
            />
          ))}
          <span className="hero-e-chip">Est. 1979</span>
        </div>
      </div>
    </section>
  );
}

/** Re-exported from the tweaks store so the variant set has one definition. */
export type { HeroVariant };

export function Hero({ variant = "B" }: { variant?: HeroVariant }) {
  const content =
    variant === "B" ? <HeroB /> :
    variant === "C" ? <HeroC /> :
    variant === "D" ? <HeroD /> :
    variant === "E" ? <HeroE /> :
    <HeroA />;
  // Marks the hero for the header's scroll observer — see Header.tsx, which
  // hides the utility bar once this leaves the viewport. A plain attribute
  // hook rather than a shared class, since each variant already owns its own
  // top-level class (.hero, .hero-b, .hero-c, ...) for its own styling.
  return <div data-hero-root>{content}</div>;
}
